import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuthDataSetter, useDispatch, useSelector } from '../redux/hooks';
import { updateLoginField, setLoginError, clearLoginForm, LoginState } from '../redux/slices/loginSlice';
import InputField from './InputField';
import { closeModal, openLogin, openRegister } from '../redux/slices/modalSlice';
import ModalWrapper from './ModalWrapper';
import { setAccessToken } from '../redux/slices/authSlice';
import { ResendVerificationButton } from './ResendVerificationButton';

const Modal = styled.div`
  width: calc(100% - 20px);
  max-width: 800px;
  background: white;
  border-radius: 32px;
  box-shadow: 0 0 20px #00000050;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 40px 10px;
`;

const StyledForm = styled.form`
  width: 100%;
  max-width: 500px;
  margin: auto;
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`\
  width: 100%;
  height: 42px;
  background-color: ${(props) => (props.disabled ? '#aabbbb' : '#3B6255')};
  color: white;
  font-weight: bold;
  font-size: 18px;
  border: none;
  border-radius: 20px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  margin-top: 14px;
`;

const ErrorText = styled.div`
  color: #AE0505;
  font-size: 10px;
  font-family: 'IBM Plex Mono';
  font-weight: 700;
  width: full;
`;

const ErrorIcon = styled.img`
  max-height: 100%;
  max-width: 100%;
`;

const ErrorContainer = styled.div`
  margin-top: -6px;
  gap: 4px;
  display: flex;
  align-items: center;
  justify-content: left;
  width: 500px;
`;

const SwitchText = styled.div`
  margin-top: 20px;
  font-family: 'IBM Plex Mono';
  font-size: 14px;
  color: #000;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    color: #3B6255;
  }
`;

const LoginModal: React.FC = () => {
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;
  const dispatch = useDispatch();
  const { email, password, error } = useSelector((state) => state.login);
  const [loading, setLoading] = useState(false);
  const setAuthData = useAuthDataSetter();

  const handleChange = (field: keyof LoginState, value: string) => {
    dispatch(updateLoginField({ field, value }));
    if (error) {
      dispatch(setLoginError(''));
    }
  };

  const switchModal = (to: 'login' | 'register') => {
    dispatch(closeModal());
    setTimeout(() => {
      if (to === 'login') dispatch(openLogin());
      else dispatch(openRegister());
    }, 100);
  };

  const handleSubmit = async () => {
    if (!email.includes('@')) {
      dispatch(setLoginError('Некоректний email'));
      return;
    }

    setLoading(true);
    dispatch(setLoginError(''));

    try {
      const res = await fetch(`${BASE_API_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, customer_password: password }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'Invalid credentials' || data.error === 'Неправильний email або пароль') {
          dispatch(setLoginError('Неправильний email або пароль'));
        } else if (data.requiresEmailVerification) {
          dispatch(setLoginError('Email не підтверджено. Ви можете повторно надіслати лист.'));
        } else if (data.error.includes('Email не підтверджено')) {
          dispatch(setLoginError('Email не підтверджено. Перевірте вашу пошту та підтвердіть email для входу.'));
        } else {
          dispatch(setLoginError(data.error || 'Помилка при логіні'));
        }
      } else {
        dispatch(setAccessToken(data.data.accessToken));
        setAuthData(data.data);
        dispatch(clearLoginForm());
        dispatch(closeModal());
        alert(`Логін успішний ${email}`);
      }
    } catch (err) {
      dispatch(setLoginError('Помилка мережі'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClick={() => dispatch(closeModal())}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <StyledForm
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <InputField label="Email" value={email} onChange={(v) => handleChange('email', v)} />
          <InputField label="Пароль" value={password} isPassword onChange={(v) => handleChange('password', v)} />
          {error && (
            <ErrorContainer>
              <ErrorIcon src={`${process.env.PUBLIC_URL}/media/Error_icon.svg`} alt="Error" />
              <ErrorText>{error}</ErrorText>
            </ErrorContainer>
          )}

          {error?.includes('не підтверджено') && email && (
            <ResendVerificationButton email={email} />
          )}
          <SubmitButton disabled={loading} onClick={handleSubmit}>
            {loading ? 'Завантаження...' : 'Увійти'}
          </SubmitButton>
        </StyledForm>
        <SwitchText onClick={() => switchModal('register')}>Зареєструватися</SwitchText>
      </Modal>
    </ModalWrapper>
  );
};

export default LoginModal;
